import React from 'react';
import styled, { css, keyframes } from 'styled-components';

// Messages that arrive live (agent replies, status lines) ease in; history doesn't
const easeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
`;
import Avatar from './Avatar';
import MessageBubble from './MessageBubble';
import AttachmentTiles from './AttachmentTiles';
import { containsHtml } from '../utils/htmlUtils';

// Messages from the same person sit close together; a new speaker starts a new block
const MessageContainer = styled.div`
  margin-bottom: ${props => (props.$lastInGroup ? '16px' : '3px')};
  ${props => props.$fresh && css`
    animation: ${easeIn} 0.3s ease-out;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  display: flex;
  align-items: flex-end;
  gap: 8px;
  ${props => props.isUser && `
    flex-direction: row-reverse;
  `}
  
  /* Ensure proper width calculation */
  width: 100%;
  box-sizing: border-box;
`;

const MessageContent = styled.div`
  flex: 1;
  min-width: 0; /* Allow text to wrap properly */
  display: flex;
  flex-direction: column;
  
  ${props => props.isUser && `
    align-items: flex-end;
  `}
`;

const LoadingDots = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  
  span {
    width: 6px;
    height: 6px;
    background: #94a3b8;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out;
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
  
  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.5;
    }
    30% {
      transform: translateY(-2px);
      opacity: 1;
    }
  }
`;

// "12:41 PM" in the client's own time zone; empty if the time is missing or unreadable
const formatTime = (timestamp) => {
  const date = timestamp ? new Date(timestamp) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : '';
};

const DeliveryNote = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: #64748b;
`;

const SenderTime = styled.span`
  font-weight: 400;
  color: #64748b;
`;

const SenderName = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  color: ${props => (props.$agent ? '#166534' : '#275386')};
  margin-bottom: 3px;
  padding-left: 2px;
`;

// Keeps a group's bubbles lined up where the avatar would be
const AvatarSpace = styled.div`
  width: 28px;
  flex-shrink: 0;
`;

// Status line such as "Priya has closed this chat" - not a bubble
// Files sit above their caption, if there is one
// Full width, so the files' bubble is capped like a text bubble and sits on the sender's side
const AttachmentSlot = styled.div`
  display: flex;
  justify-content: ${props => (props.$isUser ? 'flex-end' : 'flex-start')};
  width: 100%;
  min-width: 0;
  margin-bottom: ${props => (props.$withCaption ? '4px' : '0')};
`;

// A message's files as tiles: a run of files sent together (see ChatBot) or a single one
const attachmentItems = (message) => {
  if (message.attachments) return message.attachments;
  if (!message.attachment) return null;
  return [{ key: message.id, attachment: message.attachment, uploading: message.uploading, uploadError: message.uploadError }];
};

const SystemNote = styled.div`
  margin: 4px auto 16px;
  max-width: 85%;
  padding: 6px 12px;
  border-radius: 12px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  text-align: center;
`;

// isFirstInGroup / isLastInGroup: place in a run of messages from the same person
const Message = ({ message, isFirstInGroup = true, isLastInGroup = true, showDelivery = false }) => {
  if (message.sender === 'system') {
    // Text comes from the Aura backend, already escaped
    return <SystemNote dangerouslySetInnerHTML={{ __html: message.text || '' }} />;
  }
  const agentName = message.sender === 'agent' ? (message.senderName || '5C Support') : null;

  const messageText = message.text || '';
  const files = attachmentItems(message);
  
  // Use the isHtml flag from the Redux store (set by the improved chatSlice logic)
  const shouldTreatAsHtml = message.isHtml || false;
  
  // console.log('Message component:', {
  //   id: message.id,
  //   isHtml: message.isHtml,
  //   isStreaming: message.isStreaming,
  //   isLoading: message.isLoading,
  //   textLength: messageText.length,
  //   preview: messageText.substring(0, 50)
  // });
  
  return (
    <MessageContainer isUser={message.isUser} $lastInGroup={isLastInGroup} $fresh={message.fresh}>
      {/* The client's own messages need no avatar; others show it once, beside the group's last bubble */}
      {!message.isUser && (isLastInGroup ? <Avatar isUser={false} agentName={agentName} /> : <AvatarSpace />)}
      <MessageContent isUser={message.isUser}>
        {!message.isUser && isFirstInGroup && (
          <SenderName $agent={Boolean(agentName)}>
            {agentName || 'Aura'}
            {agentName && formatTime(message.timestamp) && <SenderTime> · {formatTime(message.timestamp)}</SenderTime>}
          </SenderName>
        )}
        {files && (
          <AttachmentSlot $withCaption={Boolean(messageText)} $isUser={message.isUser}>
            <AttachmentTiles
              items={files}
              isUser={message.isUser}
              isAgent={Boolean(agentName)}
              isFirstInGroup={isFirstInGroup}
            />
          </AttachmentSlot>
        )}
        {(messageText || !files) && (
        <MessageBubble
          isUser={message.isUser}
          isAgent={Boolean(agentName)}
          isFirstInGroup={isFirstInGroup}
          isHtml={shouldTreatAsHtml}
          isStreaming={message.isStreaming}
        >
          {messageText}
          {/* {message.isLoading && !messageText && (
            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
             <div><div>Thinking</div><LoadingDots><span></span><span></span><span></span></LoadingDots></div>
            </span>
          )} */}
        </MessageBubble>
        )}
        {message.isUser && message.sentToAgent && showDelivery && (
          <DeliveryNote>Sent ✓{formatTime(message.timestamp) ? ` · ${formatTime(message.timestamp)}` : ''}</DeliveryNote>
        )}
      </MessageContent>
    </MessageContainer>
  );
};

export default Message; 